import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { userloginDetails } from '../Utils/AdminAuth';
import axios from 'axios';
import { STAGE_URL } from '../Dashboard/api/Api';
import { modernPalette as c, modernTableStyles, modernCardStyle } from '../Utils/customTableStyles';

const ERRORS_LIST_API = `${STAGE_URL}/errors-overview`;
const ERRORS_STREAM_API = `${STAGE_URL}/errors-overview/stream`;

const formatTimestamp = (iso) => new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
});

// A lot of what lands here is a raw JSON log line from a library (kafkajs,
// body-parser, etc.) rather than a plain sentence — e.g.
// {"level":"ERROR","logger":"kafkajs","message":"[Connection] ...","error":"..."}.
// Pull out the parts worth showing at a glance; fall back to null for plain
// text messages, which just render as-is.
function parseStructuredMessage(raw) {
    if (!raw || raw[0] !== '{') return null;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        return {
            logger: parsed.logger,
            summary: parsed.message || raw,
            detail: parsed.error && parsed.error !== parsed.message ? parsed.error : null,
            broker: parsed.broker,
        };
    } catch (err) {
        return null;
    }
}

// Volatile fields (correlationId, size, exact timestamp) live outside
// `message`/`error` in these JSON logs, so the same recurring problem — e.g.
// a Kafka heartbeat rejected while the consumer is rejoining — produces an
// identical logger+summary+detail every time. That's the fingerprint used to
// collapse a burst of duplicates into one row instead of one per occurrence.
function fingerprintOf(row) {
    const structured = parseStructuredMessage(row.message);
    return structured
        ? `s:${structured.logger}|${structured.summary}|${structured.detail}|${structured.broker}`
        : `r:${row.message}`;
}

// Collapses consecutive rows (list is already newest-first) that share a
// fingerprint into one group with a count — a burst of 50 identical Kafka
// errors becomes one row reading "×50", not 50 rows of near-duplicate text.
function groupConsecutive(rows) {
    const groups = [];
    for (const row of rows) {
        const fp = fingerprintOf(row);
        const last = groups[groups.length - 1];
        if (last && last.fingerprint === fp) {
            last.count += 1;
            last.oldest = row;
        } else {
            groups.push({ fingerprint: fp, latest: row, oldest: row, count: 1 });
        }
    }
    return groups;
}

const clampStyle = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
};

const Tag = ({ children, bg, color }) => (
    <span style={{
        display: 'inline-block',
        background: bg,
        color,
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        borderRadius: '999px',
        padding: '2px 8px',
        marginRight: '8px',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
    }}>
        {children}
    </span>
);

const TimeCell = ({ group }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <span style={{
            marginTop: '6px', width: '7px', height: '7px', borderRadius: '50%',
            background: c.signal, flex: '0 0 auto',
        }} />
        <div>
            <div style={{ color: c.text, fontSize: '12.5px', fontWeight: 500 }}>
                {formatTimestamp(group.latest.createdAt)}
            </div>
            {group.count > 1 && (
                <div style={{ color: c.textFaint, fontSize: '11px', marginTop: '2px' }}>
                    since {formatTimestamp(group.oldest.createdAt)}
                </div>
            )}
        </div>
    </div>
);

const MessageCell = ({ group }) => {
    const row = group.latest;
    const structured = parseStructuredMessage(row.message);

    return (
        <div style={{ padding: '2px 0' }}>
            <div style={{ ...clampStyle, color: c.text, fontSize: '13.5px', fontWeight: 600, lineHeight: '19px' }}>
                {group.count > 1 && <Tag bg={c.signalBg} color={c.signal}>×{group.count}</Tag>}
                {structured?.logger && <Tag bg={c.tagBg} color={c.tagText}>{structured.logger}</Tag>}
                {structured ? structured.summary : row.message}
            </div>
            {structured?.detail && (
                <div style={{ color: c.signal, fontSize: '12px', fontWeight: 500, marginTop: '3px', ...clampStyle }}>
                    {structured.detail}
                </div>
            )}
            {structured?.broker && (
                <div style={{ color: c.textFaint, fontSize: '11px', marginTop: '3px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {structured.broker}
                </div>
            )}
        </div>
    );
};

// Full detail on expand: the stack trace if there is one, otherwise the raw
// message pretty-printed (structured logs are unreadable as one JSON line).
// For a grouped burst, also shows how long it's been recurring.
const ExpandedRow = ({ data: group }) => {
    const row = group.latest;
    const structured = parseStructuredMessage(row.message);
    let body = row.stack;
    if (!body) {
        body = structured ? JSON.stringify(JSON.parse(row.message), null, 2) : row.message;
    }
    return (
        <div style={{ padding: '14px 24px', background: c.headBg, borderTop: `1px solid ${c.border}` }}>
            {group.count > 1 && (
                <div style={{ color: c.textMuted, fontSize: '12px', marginBottom: '8px' }}>
                    Occurred {group.count} times in a row, from {formatTimestamp(group.oldest.createdAt)} to {formatTimestamp(row.createdAt)}. Showing the most recent occurrence below.
                </div>
            )}
            <div style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px', color: c.text,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: '18px',
            }}>
                {body || 'No further detail available.'}
            </div>
        </div>
    );
};

const AiBotErrorLogs = () => {
    const lStorage = userloginDetails();
    const adminUserId = lStorage?.user?.userId;
    const adminToken = lStorage?.token;

    const [errors, setErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [streamConnected, setStreamConnected] = useState(false);
    const [error, setError] = useState({ errorStatus: false, errorMessage: '' });

    useEffect(() => {
        fetchErrors({ page: 1, limit: perPage, q: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced search, same pattern as the admin search box on the usage tab.
    useEffect(() => {
        const timer = setTimeout(() => fetchErrors({ page: 1, limit: perPage, q: searchQuery.trim() }), 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    // Always-on live feed for as long as this tab is mounted — one SSE
    // connection, new errors prepend to the top as they happen. The browser's
    // EventSource retries automatically on drop; streamConnected just reflects
    // that state for the status pill, it isn't user-controlled.
    useEffect(() => {
        const url = `${ERRORS_STREAM_API}?adminUserId=${encodeURIComponent(adminUserId)}&adminToken=${encodeURIComponent(adminToken)}`;
        const source = new EventSource(url);

        source.onopen = () => setStreamConnected(true);

        source.onmessage = (event) => {
            try {
                const incoming = JSON.parse(event.data);
                setErrors((prev) => [{ ...incoming, _id: `live-${incoming.createdAt}-${Math.random()}` }, ...prev]);
                setTotal((prev) => prev + 1);
            } catch (err) {
                // Malformed SSE payload — skip it, don't break the live feed over one bad line.
            }
        };

        source.onerror = () => setStreamConnected(false);

        return () => source.close();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchErrors = async ({ page: pageArg, limit, q }) => {
        setIsLoading(true);
        try {
            const { data } = await axios.post(ERRORS_LIST_API, {
                adminUserId,
                adminToken,
                page: pageArg,
                limit,
                q,
            });

            if (data.success) {
                setErrors(data.errors);
                setTotal(data.total ?? 0);
                setPage(data.page ?? pageArg);
                setPerPage(data.limit ?? limit);
            } else {
                setError({ errorStatus: true, errorMessage: data.error || 'Failed to fetch errors' });
            }
        } catch (err) {
            setError({ errorStatus: true, errorMessage: err.response?.data?.error || 'Something went wrong while fetching errors' });
        }
        setIsLoading(false);
    };

    const handleChangePage = (nextPage) => {
        fetchErrors({ page: nextPage, limit: perPage, q: searchQuery.trim() });
    };

    const handleChangeRowsPerPage = (nextPerPage, nextPage) => {
        fetchErrors({ page: nextPage, limit: nextPerPage, q: searchQuery.trim() });
    };

    // Grouping only ever collapses rows already on this page — it never merges
    // across a page boundary or a live update with the fetched list, so it
    // stays a pure display concern on top of whatever `errors` currently holds.
    const groupedErrors = useMemo(() => groupConsecutive(errors), [errors]);

    const columns = [
        {
            name: 'Time',
            selector: group => group.latest.createdAt,
            cell: group => <TimeCell group={group} />,
            minWidth: '190px',
            sortable: true,
        },
        {
            name: 'Message',
            selector: group => group.latest.message,
            cell: group => <MessageCell group={group} />,
            grow: 3,
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '14px', marginBottom: '14px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', width: '260px' }}>
                    <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: c.textFaint, fontSize: '12px' }} />
                    <input
                        type="text"
                        placeholder="Search error message"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%', height: '36px', padding: '0 14px 0 34px',
                            border: `1px solid ${c.border}`, borderRadius: '20px',
                            fontSize: '13px', outline: 'none', background: '#fff', color: c.text,
                        }}
                    />
                </div>

                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: streamConnected ? c.liveBg : c.tagBg,
                    color: streamConnected ? c.live : c.textMuted,
                    fontWeight: 700, fontSize: '12px', borderRadius: '999px',
                    padding: '5px 12px', whiteSpace: 'nowrap',
                }}>
                    <span style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: streamConnected ? c.live : c.textFaint,
                    }} />
                    {streamConnected ? 'Live' : 'Connecting...'}
                </span>
            </div>

            {error.errorStatus && (
                <div className="alert alert-danger alert-dismissible" role="alert">
                    <button type="button" className="close" data-dismiss="alert" aria-label="Close" onClick={() => setError({ errorStatus: false, errorMessage: '' })}>
                        <span aria-hidden="true">&times;</span>
                    </button>
                    <div>{error.errorMessage}</div>
                </div>
            )}

            <div style={modernCardStyle}>
                <DataTable
                    columns={columns}
                    data={groupedErrors}
                    customStyles={modernTableStyles}
                    highlightOnHover
                    pagination
                    paginationServer
                    paginationTotalRows={total}
                    paginationDefaultPage={page}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    expandableRows
                    expandableRowsComponent={ExpandedRow}
                    noDataComponent={
                        <div style={{ padding: '32px', color: c.textMuted, fontSize: '13px' }}>
                            {isLoading ? 'Loading...' : 'No errors — good sign.'}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default AiBotErrorLogs;
